import React, { useCallback, useState } from "react";
import "./Quiz.css";
import { Seo } from "../../Shared/Seo/Seo.tsx";
import { IntroLead, IntroRest } from "../../Landing/IntroParagraphs.tsx";
import { findBySlug } from "../../Shared/Seo/routeSeo.ts";
import { Operations } from "../../MathFactsGenerator/Constants.ts";
import { SettingsProvider } from "../../MathFactsGenerator/Settings/SettingsStore.tsx";
import { Operators } from "../../MathFactsGenerator/Settings/Operators.tsx";
import { ValueRange } from "../../MathFactsGenerator/Settings/ValueRange.tsx";
import { canBuildDeck, flashCardSettings } from "../deck.ts";
import { createQuizQueue, QuizQueue } from "./quizQueue.ts";
import { QuizRun, QuizResult } from "./QuizRun.tsx";
import { QuizSummary } from "./QuizSummary.tsx";
import { track } from "../../Shared/analytics.ts";
import { quizEventParams } from "./quizEventParams.ts";
import { bestScoreKey, readBestScore, recordScore } from "./bestScore.ts";
import { factKey, recordMiss, recordCorrect } from "./missedFacts.ts";
import { MissedFactsPractice } from "./MissedFactsPractice.tsx";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

interface QuizPageProps {
    slug: string;
    operation: Operations;
}

interface QuizMode {
    /** Translation key for the mode's label; not itself display text. */
    labelKey: StringKey;
    /** Null for an untimed run. */
    durationMs: number | null;
}

const TIMED_MODES: QuizMode[] = [
    { labelKey: "flashCards.thirtySeconds", durationMs: 30_000 },
    { labelKey: "flashCards.oneMinute", durationMs: 60_000 },
    { labelKey: "flashCards.twoMinutes", durationMs: 120_000 },
    { labelKey: "flashCards.threeMinutes", durationMs: 180_000 },
];

interface Summary {
    result: QuizResult;
    best: number | null;
    isNewBest: boolean;
}

/**
 * One quiz route: a keyword landing page wrapped around a timed (or untimed)
 * typed-answer quiz.
 *
 * Its own route rather than a mode on the flash-card pages (ADR 0003 §5): a
 * quiz is a different activity from a card deck, wants a quiet play area, and
 * "timed math test" is its own search. Settings are still shared through
 * `SettingsContext`, so `Operators` and `ValueRange` drive the quiz unmodified.
 */
export function QuizPage({ slug, operation }: QuizPageProps) {
    const entry = findBySlug(slug)!;
    const [settings, setSettings] = useState(() => flashCardSettings(operation));

    const [mode, setMode] = useState<QuizMode | null>(null);
    const [queue, setQueue] = useState<QuizQueue | null>(null);
    const [runId, setRunId] = useState(0);
    const [summary, setSummary] = useState<Summary | null>(null);
    // Bumped when a run finishes so the missed-facts panel re-reads the store.
    const [missesVersion, setMissesVersion] = useState(0);

    const ready = canBuildDeck(settings);
    const t = useTranslation();

    function startRun(chosen: QuizMode) {
        // The queue is built here, from settings as they stand, not during
        // render - GenerateDataV2 is non-deterministic (ADR 0001 §4).
        setQueue(createQuizQueue(settings));
        setMode(chosen);
        setSummary(null);
        setRunId((id) => id + 1);
    }

    function playAgain() {
        if (mode) {
            startRun(mode);
        }
    }

    function chooseAnotherMode() {
        setMode(null);
        setQueue(null);
        setSummary(null);
    }

    const handleFinish = useCallback(
        (result: QuizResult) => {
            const key = bestScoreKey(operation, result.durationMs);
            const previous = readBestScore(key);
            const best = recordScore(key, result.correct);
            // Make the run's misses durable, and clear facts answered right
            // (ADR 0012 §2). Both, or the list never shrinks.
            result.misses.forEach((miss) => recordMiss(factKey(miss.front)));
            result.corrects.forEach((front) => recordCorrect(factKey(front)));
            setMissesVersion((v) => v + 1);
            // After the stores, deliberately: track() already swallows its own
            // failures, but ordering makes it true twice - a visitor never loses
            // a best score to analytics. ADR 0026 §2.
            track('quiz_completed', quizEventParams(operation, result));
            setSummary({
                result,
                best: best > 0 ? best : null,
                isNewBest:
                    previous !== null ? result.correct > previous : result.correct > 0,
            });
        },
        [operation]
    );

    return (
        <SettingsProvider settings={settings} setSettings={setSettings}>
            <Seo entry={entry} />
            <div className="landingContent">
                <h1>{entry.h1}</h1>
                <IntroLead paragraphs={entry.intro} />
            </div>

            <div className="quizTool">
                {!ready ? (
                    <p className="quizEmpty">
                        {t('flashCards.quizEmptyNote')}
                    </p>
                ) : summary ? (
                    <>
                        <QuizSummary
                            result={summary.result}
                            best={summary.best}
                            isNewBest={summary.isNewBest}
                            onPlayAgain={playAgain}
                        />
                        <button
                            type="button"
                            className="quizChangeMode"
                            onClick={chooseAnotherMode}
                        >
                            {t('flashCards.chooseDifferentLength')}
                        </button>
                    </>
                ) : mode && queue ? (
                    <QuizRun
                        key={runId}
                        queue={queue}
                        durationMs={mode.durationMs}
                        onFinish={handleFinish}
                    />
                ) : (
                    <div className="quizModePicker">
                        <p className="quizModePrompt">{t('flashCards.howLongDoYouWant')}</p>
                        <div className="quizModeButtons">
                            {TIMED_MODES.map((option) => (
                                <button
                                    key={option.labelKey}
                                    type="button"
                                    onClick={() => startRun(option)}
                                >
                                    {t(option.labelKey)}
                                </button>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="quizModeUntimed"
                            onClick={() => startRun({ labelKey: "flashCards.noClockJustPractise", durationMs: null })}
                        >
                            {t('flashCards.noClockJustPractise')}
                        </button>
                    </div>
                )}

                {/* Practice built from the facts missed on this browser's quiz
                    runs. Renders nothing until there is something to practise. */}
                {(!mode || summary) && <MissedFactsPractice refreshKey={missesVersion} />}

                {/* Hidden during a run - changing settings mid-run does nothing
                    until the next one, so it would only be a distraction. */}
                {(!mode || summary) && (
                    <details className="quizSettings">
                        <summary>{t('flashCards.changeWhatToPractise')}</summary>
                        <Operators />
                        <div className="valueRangeContainer">
                            <ValueRange RangeSettings={0} />
                            <ValueRange RangeSettings={1} />
                        </div>
                    </details>
                )}
            </div>

            <IntroRest paragraphs={entry.intro} />

            {entry.faq && entry.faq.length > 0 && (
                <section className="landingFaq">
                    <h2>{t('flashCards.frequentlyAskedQuestions')}</h2>
                    {entry.faq.map((item, i) => (
                        <div key={i} className="faqItem">
                            <h3>{item.q}</h3>
                            <p>{item.a}</p>
                        </div>
                    ))}
                </section>
            )}
        </SettingsProvider>
    );
}

export default QuizPage;
